import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useAgentDnaMetrics } from "./data.ts";
import { MetricTiles } from "./components.tsx";
import "./agentdna.css";

/**
 * Observability (AgentDNA port): the org-wide, real-time view across agents, apps, intents and
 * interactions — the headline numbers and the panels that summarize activity and threats. Viewer-
 * level; the server enforces access.
 *
 * UI-only port: wired to a contract-shaped stub, so the panels show their honest zero/empty state
 * until the real reads (and the live-stream feed) land. The interaction-flow graph the dashboard
 * draws here is a later addition once the data is wired.
 */
export function ObservabilityScreen(): ReactNode {
  const { role } = useIdentity();
  const { data: metrics, loading } = useAgentDnaMetrics();

  const state = resolveState(role, loading);

  return (
    <ScreenScaffold
      title="Observability"
      subtitle="Real-time view across agents, apps, intents and interactions."
      state={state}
    >
      <MetricTiles
        tiles={[
          { label: "Agents", value: metrics.agentCount },
          { label: "Apps & tools", value: metrics.appCount },
          { label: "Intents", value: metrics.intentCount },
          { label: "Interactions", value: metrics.interactionsCount },
          { label: "Threats", value: metrics.threatCount },
          { label: "Avg reliability", value: metrics.avgReliability },
        ]}
      />
      <div className="adna-obs-grid">
        <section className="adna-panel" aria-label="Activity">
          <h2 className="adna-panel__title">Activity</h2>
          <div className="state-box" data-kind="empty">
            <strong>No activity yet</strong>
            <span>Interaction volume will appear here once agents begin reporting.</span>
          </div>
        </section>
        <section className="adna-panel" aria-label="Threats">
          <h2 className="adna-panel__title">Threats</h2>
          <div className="state-box" data-kind="empty">
            <strong>No threats detected</strong>
            <span>Flagged interactions and their severity will appear here.</span>
          </div>
        </section>
        <section className="adna-panel" aria-label="Interaction flow">
          <h2 className="adna-panel__title">Interaction flow</h2>
          <div className="state-box" data-kind="empty">
            <strong>No flow to graph yet</strong>
            <span>The agent-to-agent and agent-to-tool flow graph appears once interactions exist.</span>
          </div>
        </section>
      </div>
    </ScreenScaffold>
  );
}

function resolveState(role: ReturnType<typeof useIdentity>["role"], loading: boolean): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Observability needs at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  return { kind: "ready" };
}
